import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, FileText, Layers, Microscope, FileSearch, Award,
  Library, Stethoscope, MessageSquare, PenTool, CheckCircle2, ArrowRight,
  Loader2, Sparkles, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const PROJECT_TYPES = [
  { id: 'dissertation',        icon: BookOpen,      label: 'Dissertation',         hint: 'Doctoral or master thesis' },
  { id: 'research_article',    icon: FileText,      label: 'Research Article',     hint: 'Journal manuscript' },
  { id: 'systematic_review',   icon: Layers,        label: 'Systematic Review',    hint: 'PRISMA-style review' },
  { id: 'meta_analysis',       icon: Microscope,    label: 'Meta-analysis',        hint: 'Quantitative synthesis' },
  { id: 'clinical_protocol',   icon: FileSearch,    label: 'Clinical Protocol',    hint: 'Trial protocol / SAP' },
  { id: 'grant_proposal',      icon: Award,         label: 'Grant Proposal',       hint: 'NIH / Horizon / ERC' },
  { id: 'book',                icon: Library,       label: 'Book / Monograph',     hint: 'Academic book or chapter' },
  { id: 'case_report',         icon: Stethoscope,   label: 'Case Report',          hint: 'Single-patient case' },
  { id: 'conference_abstract', icon: MessageSquare, label: 'Conference Abstract',  hint: 'Poster / oral abstract' },
  { id: 'editorial',           icon: PenTool,       label: 'Editorial / Commentary', hint: 'Opinion piece' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
];

export default function ProjectCreationWizard({ open, onOpenChange, userId, onCreated }) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState('research_article');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('en');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');

  const reset = () => {
    setStep(1);
    setType('research_article');
    setTitle('');
    setDescription('');
    setLanguage('en');
    setSuggestion(null);
    setSelectedGuideline(null);
    setTemplateId('');
  };

  // Step 3 also offers a ready structure, so the project starts with sections in place
  useEffect(() => {
    if (step !== 3) return;
    thesisApi.listStructureTemplates(userId, type)
      .then((d) => setTemplates([...(d.templates || []), ...(d.builtin || [])]))
      .catch(() => setTemplates([]));
  }, [step, type, userId]);

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // ---- Step 2 → Step 3: ask the AI to suggest guidelines
  const handleSuggestGuidelines = async () => {
    if (!title.trim() || !description.trim() || description.trim().length < 30) {
      toast.error('Provide a title and at least 30 characters describing your study.');
      return;
    }
    setSuggesting(true);
    try {
      const data = await thesisApi.suggestGuidelines(userId, type, description.trim(), language);
      setSuggestion(data);
      setSelectedGuideline(data.primary_recommendation_id || (data.top_3?.[0]?.id ?? null));
      setStep(3);
    } catch (e) {
      toast.error('Guideline suggestion failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSuggesting(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const project = await thesisApi.createProject(userId, {
        owner_id: userId,
        title: title.trim(),
        research_question: '',
        description: description.trim(),
        project_type: type,
        primary_guideline_id: selectedGuideline,
        language,
      });
      const tpl = templates.find((t) => t.id === templateId);
      if (tpl) {
        try {
          const res = await thesisApi.applyDissertationStructure(
            userId, project.id,
            (tpl.chapters || []).map((c, i) => ({
              order: i, title: c.title, description: c.description || '',
              rationale: c.rationale || '', subtopics: [], suggested_evidence_keywords: [],
              depth: c.depth || 0, parent_temp_id: c.parent_temp_id || null,
              temp_id: c.temp_id || `tmp_${i}`,
            })), false);
          toast.success(`Project "${project.title}" created with ${res.inserted} sections`);
        } catch {
          toast.warning(`Project created, but the template could not be applied — use AI Structure in the Writing tab.`);
        }
      } else {
        toast.success(`Project "${project.title}" created`);
      }
      reset();
      onOpenChange(false);
      onCreated && onCreated(project);
    } catch (e) {
      toast.error('Project creation failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="project-wizard">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold">
              Step {step} of 3
            </span>
            <div className="flex-1 h-1 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#1d4ed8] transition-all" style={{ width: `${(step / 3) * 100}%` }} />
            </div>
          </div>
          <DialogTitle className="font-serif text-2xl" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            {step === 1 && 'What are you writing?'}
            {step === 2 && 'Describe your scientific project'}
            {step === 3 && 'Choose your reporting guideline'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Pick the type of scholarly output. InkPhD adapts its workflow, structure, and reporting checks to match.'}
            {step === 2 && 'Tell us about your study design, population, and intent. Our AI will infer the best EQUATOR reporting guideline.'}
            {step === 3 && 'We analysed your project and ranked the top 3 EQUATOR guidelines. Pick the one you will follow.'}
          </DialogDescription>
        </DialogHeader>

        {/* ---------- STEP 1: project type ---------- */}
        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="wizard-step-1">
            {PROJECT_TYPES.map((p) => {
              const Icon = p.icon;
              const active = type === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setType(p.id)}
                  className={`flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${
                    active
                      ? 'border-[#1d4ed8] bg-[#1d4ed8]/5 dark:bg-[#1d4ed8]/10 shadow-sm'
                      : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
                  }`}
                  data-testid={`wizard-type-${p.id}`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${active ? 'text-[#1d4ed8] dark:text-[#7aa9ff]' : 'text-slate-500'}`} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-medium ${active ? 'text-[#0b1a35] dark:text-white' : 'text-foreground'}`}>{p.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.hint}</div>
                  </div>
                  {active && <CheckCircle2 className="w-4 h-4 text-[#1d4ed8] dark:text-[#7aa9ff] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        )}

        {/* ---------- STEP 2: description ---------- */}
        {step === 2 && (
          <div className="space-y-4 mt-4" data-testid="wizard-step-2">
            <div>
              <Label htmlFor="wizard-title" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Project title
              </Label>
              <Input
                id="wizard-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Outcomes of neoadjuvant chemotherapy in breast cancer (2018-2024)"
                className="mt-1.5"
                data-testid="wizard-title-input"
              />
            </div>
            <div>
              <Label htmlFor="wizard-description" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Describe your scientific project
              </Label>
              <Textarea
                id="wizard-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Study design, population, primary outcome, methodology, intent..."
                rows={6}
                className="mt-1.5 leading-relaxed"
                data-testid="wizard-description-input"
              />
              <p className="text-[11px] text-muted-foreground mt-2">
                Example: "Retrospective cohort study of 1,200 patients across 5 hospitals, comparing
                pathological complete response rates after AC-T vs TCH chemotherapy regimens. Primary outcome: pCR rate."
              </p>
            </div>
            <div>
              <Label htmlFor="wizard-language" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Writing language
              </Label>
              <select
                id="wizard-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="wizard-language-select"
              >
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* ---------- STEP 3: guideline picker ---------- */}
        {step === 3 && (
          <div className="space-y-3 mt-4" data-testid="wizard-step-3">
            <div className="rounded-lg border border-slate-200 dark:border-white/10 p-3">
              <label htmlFor="wizard-template" className="text-xs font-semibold uppercase
                tracking-widest text-muted-foreground">
                Start with a ready structure (optional)
              </label>
              <select
                id="wizard-template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="wizard-template-select"
              >
                <option value="">No structure — I will build it later</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.chapters?.length || 0} sections){t.builtin ? '' : ' · my template'}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                The project is created with these sections already in place — you can edit, reorder
                or replace them at any time.
              </p>
            </div>
            {suggestion?.top_3?.length ? suggestion.top_3.map((g, i) => {
              const active = selectedGuideline === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedGuideline(g.id)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    active
                      ? 'border-[#1d4ed8] bg-[#1d4ed8]/5 dark:bg-[#1d4ed8]/10 shadow-sm'
                      : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
                  }`}
                  data-testid={`wizard-guideline-${g.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {i === 0 && <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                      <span className="font-serif text-base font-semibold text-[#0b1a35] dark:text-white truncate" style={{ fontFamily: '"Fraunces", serif' }}>
                        {g.name}
                      </span>
                      {i === 0 && <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 text-[10px]">Top pick</Badge>}
                    </div>
                    <div className="flex items-baseline gap-1 flex-shrink-0">
                      <span className="text-2xl font-serif font-bold text-[#1d4ed8] dark:text-[#7aa9ff]" style={{ fontFamily: '"Fraunces", serif' }}>{g.fit_score}</span>
                      <span className="text-xs text-muted-foreground">/100</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{g.reason}</p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
                    {g.strengths?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-semibold mb-1">Strengths</div>
                        <ul className="space-y-0.5 text-muted-foreground">
                          {g.strengths.slice(0, 3).map((s, j) => <li key={j} className="flex gap-1"><span className="text-emerald-500">+</span><span>{s}</span></li>)}
                        </ul>
                      </div>
                    )}
                    {g.limitations?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400 font-semibold mb-1">Limitations</div>
                        <ul className="space-y-0.5 text-muted-foreground">
                          {g.limitations.slice(0, 2).map((s, j) => <li key={j} className="flex gap-1"><span className="text-amber-500">−</span><span>{s}</span></li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                  {active && (
                    <div className="mt-3 flex items-center gap-1.5 text-[#1d4ed8] dark:text-[#7aa9ff] text-xs font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Selected as primary guideline
                    </div>
                  )}
                </button>
              );
            }) : (
              <div className="flex items-center gap-2 text-sm text-amber-600 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <AlertCircle className="w-4 h-4" /> AI did not return any recommendations. You can skip and pick later in Settings.
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-6 pt-4 border-t border-border">
          <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : handleClose(false)} data-testid="wizard-back-btn">
            {step > 1 ? '← Back' : 'Cancel'}
          </Button>
          <div className="flex gap-2">
            {step === 1 && (
              <Button onClick={() => setStep(2)} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="wizard-next-1">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={handleSuggestGuidelines} disabled={suggesting} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="wizard-next-2">
                {suggesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing project...</> : <>Suggest guidelines <Sparkles className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handleCreate} disabled={creating} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="wizard-create-btn">
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <>Create project <CheckCircle2 className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
